import { useCallback, useMemo, useState } from 'react';

import {
  BrandedCreditCardInfo,
  BrandedCreditCardInfoStatusCode,
  BrandedCreditCardInfoTypeLabel,
  ELIGIBLE_TYPES,
} from '~/pdp/components/SunbitModal/constants';
import featureNames from '~/shared/constants/featureFlags';
import useHasFeature from '~/shared/utils/useHasFeature';
import { useMembership } from '~/techstyle-shared/react-accounts';

import { useSunbitStore } from './useSunbitStore';

interface UseSunbitModalResult {
  showModalIfEligible: () => void;
  eligibleVariant: BrandedCreditCardInfoTypeLabel | null;
  isModalOpen: boolean;
  closeModal: () => void;
  brandedCreditCardInfo: BrandedCreditCardInfo | null;
}

/**
 * Hook for managing the Sunbit modal eligibility and display.
 *
 * This hook provides:
 * - Eligibility checking based on membership data and feature flags
 * - Modal state management (open/close)
 * - showModalIfEligible callback to trigger the modal after add-to-bag
 *
 * The modal will show when:
 * - The Sunbit feature flag is enabled
 * - The user has brandedCreditCardInfo with PRE_APPROVED or INVITATION_TO_APPLY status
 * - The invitation has not expired
 * - The modal hasn't been shown before in the current login session
 */
export function useSunbitModal(): UseSunbitModalResult {
  const { hasShownModal, setHasShownModal } = useSunbitStore();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const membership = useMembership();

  const { enabled: isSunbitEnabled } = useHasFeature(featureNames.SUNBIT_WEB);

  const brandedCreditCardInfo = (membership as any)?.brandedCreditCardInfo as
    | BrandedCreditCardInfo
    | undefined;

  const invitationTypeLabel = brandedCreditCardInfo?.invitationTypeLabel;

  const creditCartStatusCode = brandedCreditCardInfo?.statuscode;

  // Determine if the user has an eligible variant
  const eligibleVariant = useMemo(() => {
    if (invitationTypeLabel && ELIGIBLE_TYPES.includes(invitationTypeLabel)) {
      return invitationTypeLabel;
    }

    return null;
  }, [invitationTypeLabel]);

  // Check if the user is eligible to see the modal
  const isEligible = useMemo(() => {
    // Should not show modal if:
    // - the modal has already been shown (persisted across page loads, reset on login/signup)
    // - the user is not eligible (no valid invitation type)
    // - the Sunbit feature flag is not enabled
    if (hasShownModal || !eligibleVariant || !isSunbitEnabled) {
      return false;
    }

    const shouldShow = [BrandedCreditCardInfoStatusCode.PENDING].includes(
      creditCartStatusCode
    );

    if (!shouldShow) {
      return false;
    }

    return shouldShow;
  }, [hasShownModal, eligibleVariant, isSunbitEnabled, creditCartStatusCode]);

  const showModalIfEligible = useCallback(() => {
    if (isEligible) {
      setHasShownModal(true);
      setIsModalOpen(true);
    }
  }, [isEligible, setHasShownModal]);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  return {
    showModalIfEligible,
    eligibleVariant,
    isModalOpen,
    closeModal,
    brandedCreditCardInfo: brandedCreditCardInfo ?? null,
  };
}

export default useSunbitModal;
